import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
    HighlightedSection,
    PadContainer,
    HorizontalFlex,
    Button,
} from 'smartsuite-ui';
import BuildFilters from './BuildFilters';
import { useLabelContext } from '../shared/hooks/labelContext';
import TimeRangeSelector from './TimeRangeSelector';
import MeasureFilters from '../shared/filtering/MeasureFilters';
import RecordLimit from '../shared/filtering/RecordLimit';
import {
    areTimeRangeFieldsValid,
    requiredFiltersHaveValues,
    areWhereClausesValid,
    isRecordLimitValid,
} from '../shared/filtering/filtersModalUtils';

export default function FiltersContainer(props) {
    const { dashboardLabels, interpolateString } = useLabelContext();

    const requiredFilterKeys = Object.keys(
        _.pickBy(props.filterConfigurations, (filter) => filter.isRequired)
    );

    const isStateInvalid = useCallback(
        () =>
            !areTimeRangeFieldsValid(
                props.timeAndPrecision,
                props.timeAndPrecisionConfiguration.timeAndPrecisionDomains,
                interpolateString,
                dashboardLabels
            ) ||
            !requiredFiltersHaveValues(
                requiredFilterKeys,
                props.filterValues,
                props.filterConfigurations,
                props.selectOptions
            ) ||
            !areWhereClausesValid(props.whereClauses) ||
            !isRecordLimitValid(props.recordLimit),
        [
            interpolateString,
            dashboardLabels,
            props.filterConfigurations,
            requiredFilterKeys,
            props.timeAndPrecisionConfiguration.timeAndPrecisionDomains,
            props.selectOptions,
            props.filterValues,
            props.recordLimit,
            props.timeAndPrecision,
            props.whereClauses,
        ]
    );

    return (
        <div className="page-filters">
            <HighlightedSection>
                <TimeRangeSelector
                    timeAndPrecisionConfiguration={
                        props.timeAndPrecisionConfiguration
                    }
                    saveTimeAndPrecision={props.saveTimeAndPrecision}
                    timeAndPrecision={props.timeAndPrecision}
                />
                <BuildFilters
                    filterConfigurations={props.filterConfigurations}
                    filterValues={props.filterValues}
                    saveFilters={props.saveFilters}
                    selectOptions={props.selectOptions}
                />
                <MeasureFilters
                    measures={props.whereClauseConfiguration.whereClauseOptions}
                    saveWhereClauses={props.saveWhereClauses}
                    showMeasureFilters={
                        props.whereClauseConfiguration.showMeasureFilters
                    }
                    whereClauses={[...props.whereClauses, { values: [''] }]}
                />
                <RecordLimit
                    recordLimitConfig={props.recordLimit}
                    saveRecordLimit={props.saveRecordLimit}
                    showRecordLimit={props.showRecordLimit}
                />
                <PadContainer cssClassModifiers={['pad-medium']}>
                    <HorizontalFlex cssClassModifiers={['justify-flex-end']}>
                        <Button
                            className="apply_filters"
                            onClick={() => {
                                props.onSearch();
                            }}
                            disabled={isStateInvalid()}
                        >
                            Search
                        </Button>
                    </HorizontalFlex>
                </PadContainer>
                <hr className="divider divider--criteria" />
            </HighlightedSection>
        </div>
    );
}

FiltersContainer.propTypes = {
    filterConfigurations: PropTypes.instanceOf(Object),
    filterValues: PropTypes.instanceOf(Object),
    onSearch: PropTypes.func,
    recordLimit: PropTypes.instanceOf(Object).isRequired,
    saveFilters: PropTypes.func.isRequired,
    saveRecordLimit: PropTypes.func.isRequired,
    saveTimeAndPrecision: PropTypes.func,
    saveWhereClauses: PropTypes.func,
    selectOptions: PropTypes.instanceOf(Array),
    showRecordLimit: PropTypes.bool.isRequired,
    timeAndPrecision: PropTypes.instanceOf(Object).isRequired,
    timeAndPrecisionConfiguration: PropTypes.instanceOf(Object).isRequired,
    whereClauseConfiguration: PropTypes.instanceOf(Object),
    whereClauses: PropTypes.instanceOf(Array).isRequired,
};

FiltersContainer.defaultProps = {
    filterConfigurations: {},
    filterValues: {},
};
